import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  ReactElement,
  forwardRef,
  HTMLAttributes,
} from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"



/* ---------------------------------- Types ---------------------------------*/
interface DropdownMenuContextType {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.MutableRefObject<HTMLElement | null>
}

const DropdownMenuContext = createContext<DropdownMenuContextType | null>(null)

function useDropdownContext(component: string) {
  const ctx = useContext(DropdownMenuContext)
  if (!ctx) {
    throw new Error(`[${component}] must be used within <DropdownMenu>`)
  }
  return ctx
}

/* ---------------------------------- Root ----------------------------------*/
interface DropdownMenuProps {
  children: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const setOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(value)
      }
      onOpenChange?.(value)
    },
    [isControlled, onOpenChange]
  )

  const triggerRef = useRef<HTMLElement | null>(null)

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </DropdownMenuContext.Provider>
  )
}

/* -------------------------------- Trigger ---------------------------------*/
interface DropdownMenuTriggerProps {
  children: ReactElement
  asChild?: boolean
  className?: string
}

const DropdownMenuTrigger = forwardRef<HTMLElement, DropdownMenuTriggerProps>(
  ({ children, asChild = false, className, ...props }, ref) => {
    const { open, setOpen, triggerRef } = useDropdownContext(
      "DropdownMenuTrigger"
    )

    const mergedRef = (node: HTMLElement | null) => {
      if (typeof ref === "function") {
        (ref as (instance: HTMLElement | null) => void)(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }
      triggerRef.current = node;
    }

    const handleClick = () => setOpen(!open)

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as ReactElement, {
        ref: mergedRef,
        className: cn((children as ReactElement<{ className?: string }>).props?.className, className),
        onClick: (...args: unknown[]) => {
          const childOnClick = (children as ReactElement<{ onClick?: (...args: unknown[]) => void }>).props?.onClick;
          childOnClick?.(...args);
          handleClick();
        },
        "aria-haspopup": "menu" as const,
        "aria-expanded": open,
        ...props,
      } as Partial<unknown>)
    }

    return (
      <button
        ref={mergedRef as any}
        onClick={handleClick}
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        {...props as any}
      >
        {children}
      </button>
    )
  }
)
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

/* -------------------------------- Content ---------------------------------*/
interface DropdownMenuContentProps extends HTMLAttributes<HTMLDivElement> {
  align?: "start" | "center" | "end"
  sideOffset?: number
}

const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  (
    {
      children,
      className,
      align = "start",
      sideOffset = 4,
      style,
      ...props
    },
    ref
  ) => {
    const { open, setOpen, triggerRef } = useDropdownContext("DropdownMenuContent")
    const contentRef = useRef<HTMLDivElement | null>(null)

    // allow parent ref
    useEffect(() => {
      if (typeof ref === "function") ref(contentRef.current)
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = contentRef.current
    }, [ref])

    // Close on outside click
    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        if (
          contentRef.current &&
          !contentRef.current.contains(e.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target as Node)
        ) {
          setOpen(false)
        }
      }
      if (open) {
        window.addEventListener("mousedown", handleClickOutside)
      }
      return () => window.removeEventListener("mousedown", handleClickOutside)
    }, [open, setOpen, triggerRef])

    // Close on Esc
    useEffect(() => {
      function handleEsc(e: KeyboardEvent) {
        if (e.key === "Escape") setOpen(false)
      }
      if (open) {
        window.addEventListener("keydown", handleEsc)
      }
      return () => window.removeEventListener("keydown", handleEsc)
    }, [open, setOpen])

    // Positioning
    const [coords, setCoords] = useState<{ top: number; left: number }>({
      top: 0,
      left: 0,
    })

    useEffect(() => {
      if (!open || !triggerRef.current || !contentRef.current) return

      const rect = triggerRef.current.getBoundingClientRect()
      const contentRect = contentRef.current.getBoundingClientRect()

      let left = rect.left
      if (align === "center") {
        left = rect.left + rect.width / 2 - contentRect.width / 2
      } else if (align === "end") {
        left = rect.left + rect.width - contentRect.width
      }

      const top = rect.bottom + sideOffset
      setCoords({ left, top })
    }, [open, align, sideOffset, triggerRef])

    if (!open) return null

    const menu = (
      <div
        ref={contentRef}
        role="menu"
        style={{ position: "absolute", top: coords.top, left: coords.left, ...style }}
        className={cn(
          "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )

    return createPortal(menu, document.body)
  }
)
DropdownMenuContent.displayName = "DropdownMenuContent"

/* --------------------------------- Common ---------------------------------*/
interface DropdownMenuItemProps extends HTMLAttributes<HTMLDivElement> {
  inset?: boolean
}

const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, inset, onClick, children, ...props }, ref) => {
    const { setOpen } = useDropdownContext("DropdownMenuItem")

    const handleSelect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      onClick?.(e)
      setOpen(false)
    }

    return (
      <div
        ref={ref}
        role="menuitem"
        tabIndex={0}
        onClick={handleSelect}
        className={cn(
          "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
          inset && "pl-8",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
DropdownMenuItem.displayName = "DropdownMenuItem"

/* ---------------------- Light stubs for rarely-used parts ------------------*/
const stub = (name: string) => {
  const Comp = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, children, ...props }, ref) => (
      <div ref={ref} className={className} {...props}>
        {children}
      </div>
    )
  )
  Comp.displayName = name
  return Comp
}

const DropdownMenuLabel = stub("DropdownMenuLabel")
const DropdownMenuSeparator = ({ className }: { className?: string }) => (
  <div className={cn("-mx-1 my-1 h-px bg-muted", className)} />
)
DropdownMenuSeparator.displayName = "DropdownMenuSeparator"

const DropdownMenuCheckboxItem = stub("DropdownMenuCheckboxItem")
const DropdownMenuRadioItem = stub("DropdownMenuRadioItem")
const DropdownMenuGroup = stub("DropdownMenuGroup")
const DropdownMenuPortal = ({ children }: { children: ReactNode }) => children
const DropdownMenuSub = stub("DropdownMenuSub")
const DropdownMenuSubContent = stub("DropdownMenuSubContent")
const DropdownMenuSubTrigger = stub("DropdownMenuSubTrigger")
const DropdownMenuRadioGroup = stub("DropdownMenuRadioGroup")

/* -------------------------------- Shortcut --------------------------------*/
const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
    {...props}
  />
)
DropdownMenuShortcut.displayName = "DropdownMenuShortcut"

/* --------------------------------- Exports --------------------------------*/
export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
